import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, QueryList, Renderer2, SimpleChanges, ViewChild, ViewChildren, ElementRef } from '@angular/core';
import { MatSlider } from '@angular/material/slider';
import { DomSanitizer } from '@angular/platform-browser';
import mixpanel from 'mixpanel-browser';
import { AppState } from '../app.service';
import { CordovaService } from '../cordova.service';
import { PollService } from '../poll/poll.service';
import { ToasterService } from '../toaster.service';
import { HomeUiService } from '../new-design/home-ui.service';
import { I18n } from '../i18n.service';
import { animate, query, stagger, style, transition, trigger } from '@angular/animations';
import { expandOnEnterAnimation, slideInDownAnimation } from 'angular-animations';
import { Keyboard } from '@awesome-cordova-plugins/keyboard/ngx';
import { Platform } from '@ionic/angular';

@Component({
  selector: 'first90-survey-nps',
  templateUrl: './survey-nps.component.html',
  styleUrls: ['./survey-nps.component.scss'],
  animations: [
    expandOnEnterAnimation({ duration: 400 }),
    slideInDownAnimation({ duration: 300 }),
    trigger('animateContent', [
      transition(':enter', [
        query(':scope > *', [
          style({ opacity: 0, transform: 'translateY(8px)' })
        ], { optional: true }),
        query(':scope > *', [
          stagger(100, [
            animate('800ms cubic-bezier(0.76, 0, 0.24, 1)', style({ opacity: 1, transform: 'translateY(0)' }))
          ])
        ], { optional: true })
      ]),
      transition(':leave', [
        animate('0.3s cubic-bezier(0.76, 0, 0.24, 1)', style({ opacity: 0, transform: 'translateY(-75px)' }))
      ])
    ])
  ]
})
export class SurveyNpsComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @Input() poll: any;
  @Input() isPreview: boolean;
  @Input() inverted = false;
  @Input() enable = false;
  @Input() progressItems = [];
  @Input() currentIndex = 0;
  @Input() screenId: any;
  @Input() chapterIndex: number;
  @Input() stepIndex: number;
  @Input() animationClass: boolean = false;
  @Output() scaleEvent = new EventEmitter<any>();
  @Output() blurCard = new EventEmitter<any>();

  @ViewChild('slider') slider: MatSlider;
  @ViewChild('disableListener', { static: false }) disableListener!: ElementRef;
  @ViewChild('feedbackTextarea', { static: false }) feedbackTextarea!: ElementRef;
  @ViewChildren('questionRef') questionRefs: QueryList<ElementRef>;

  lowerScale = 0;
  higherScale = 10;
  lowLabel = '';
  highLabel = '';
  npsRating = 0;
  scaleValue: number;
  feedbackText = '';
  enableFinal = false;
  showMenu = false;
  isEdit = false;
  isMobile: boolean;
  isMobileWeb = false;
  isMobileWebIos = false;
  innerQuestionHtml: any;
  innerOpenResponsePromptHtml: any;
  private isSaving = false;

  constructor(
    private pollService: PollService,
    private appState: AppState,
    private cordovaService: CordovaService,
    private tostrService: ToasterService,
    private i18n: I18n,
    private cdRef: ChangeDetectorRef,
    public homeUIService: HomeUiService,
    private render: Renderer2,
    private _sanitizer: DomSanitizer,
    private keyboard: Keyboard,
    private platform: Platform,
  ) {
    this.isMobile = this.appState.get('isMobile');
  }

  ngOnInit() {
    this.isMobileWeb = this.appState.get('MobileWeb');
    this.isMobileWebIos = this.appState.get('MobileWebIos');
    // if (this.isMobile) {
    //   this.resizeHeight();
    //   this.restoreResizeHeight();
    // }
    this.innerQuestionHtml = this._sanitizer.bypassSecurityTrustHtml(this.poll?.screen?.content?.question ?? '');
    this.innerOpenResponsePromptHtml = this.poll?.screen?.content?.openResponsePrompt ?? '';

    const content = this.poll?.screen?.content;
    if (content?.lowerScale != null && content?.higherScale != null) {
      this.lowerScale = content.lowerScale;
      this.higherScale = content.higherScale;
    }
    if (content?.lowLabel) { this.lowLabel = content.lowLabel; }
    if (content?.highLabel) { this.highLabel = content.highLabel; }

    const cached = this.appState.get('npsSelections')?.[this.poll?.screenContentId];
    if (this.poll?.screen?.isCompleted || this.poll?.screen?.userResponse) {
      this.npsRating = this.poll?.screen?.userResponse?.npsRating ?? 0;
      this.scaleValue = this.npsRating;
      const serverFeedback = this.poll?.screen?.userResponse?.npsFeedback ?? '';
      // Cache holds the user's latest unsaved edits — always prefer it over server value
      this.feedbackText = cached?.feedback ?? serverFeedback;
      if (cached?.feedback != null || !this.feedbackText?.trim()) {
        this.isEdit = true;
        this.enableFinal = this.scaleValue > 0;
      }
    } else if (cached != null) {
      this.scaleValue = cached.rating;
      this.npsRating = cached.rating;
      this.feedbackText = cached.feedback ?? '';
      this.enableFinal = this.scaleValue > 0;
    }
  }

  ngAfterViewInit() {
    this.questionRefs?.changes?.subscribe(() => {
      setTimeout(() => this.changeSubTextFont());
    });
    this.changeSubTextFont();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['poll']) {
      this.innerQuestionHtml = this._sanitizer.bypassSecurityTrustHtml(this.poll?.screen?.content?.question ?? '');
      this.innerOpenResponsePromptHtml = this.poll?.screen?.content?.openResponsePrompt ?? '';
      setTimeout(() => this.changeSubTextFont());
    }
    if (!changes['poll'] || changes['poll'].firstChange) { return; }
  }

  @HostListener('document:click', ['$event'])
  clickout(event: MouseEvent) {
    if (this.disableListener?.nativeElement?.contains(event.target)) {
      this.showMenu = !this.showMenu;
    } else {
      this.showMenu = false;
    }
  }

  editFeedback() {
    this.isEdit = true;
    this.updateEnableFinal();
    // Textarea is inside a *ngIf — wait one tick for Angular to render it.
    // preventScroll: true stops the parent scroll container from auto-scrolling
    // the textarea into view, which was causing the visible content bounce.
    setTimeout(() => {
      this.feedbackTextarea?.nativeElement?.focus({ preventScroll: true });
    }, 100);
  }

  @HostListener('click', ['$event'])
  onLinkClick(e) {
    if (e.target.tagName.toLowerCase() === 'a' && e.target.target === '_blank') {
      if (this.cordovaService.onCordova) {
        e.stopPropagation();
        e.preventDefault();
        this.cordovaService.openLinkInBrowser(e.target.href);
      } else {
        window.open(e.target.href, '_blank');
      }
    }
  }

  // Once the scale is stored on the server it is locked (edit-once). mat-slider's
  // [disabled] only blocks its native input; on mobile the custom touch handlers below
  // still fire, so they must bail out too — otherwise a drag would silently change the
  // already-saved rating.
  private get isScaleLocked(): boolean {
    return !!this.poll?.screen?.isCompleted;
  }

  onInputChange(event: any) {
    if (this.isScaleLocked) { return; }
    this.scaleValue = Number(event?.target ? event?.target.value : event?.value);
    this.npsRating = this.scaleValue;
    this.updateEnableFinal();
    this.persistCache();
  }

  onTouchMove(event: TouchEvent, slider: MatSlider) {
    if (this.isScaleLocked) { return; }
    const touch = event.touches[0];
    if (slider) {
      this.scaleValue = this.calculateValue(touch.clientX);
      this.npsRating = this.scaleValue;
      this.updateEnableFinal();
      this.cdRef.detectChanges();
    }
  }

  onTouchEnd(event: any) {
    if (this.isScaleLocked) { return; }
    const touch = event.changedTouches;
    if (touch?.length > 0) {
      this.scaleValue = this.calculateValue(touch[0]?.clientX);
      this.npsRating = this.scaleValue;
      this.updateEnableFinal();
      this.cdRef.detectChanges();
      this.persistCache();
    }
  }

  onFeedbackChange() {
    this.updateEnableFinal();
    this.persistCache();
  }

  private updateEnableFinal() {
    // Feedback is optional — selecting a scale value is enough to enable Finalize.
    // (Re-editing an already-saved response still blocks an empty-feedback submit
    // via the guard in finalize().)
    this.enableFinal = (this.scaleValue ?? 0) > 0;
  }

  private isDataUnchanged(): boolean {
    const saved = this.poll?.screen?.userResponse;
    if (!saved) { return false; }
    return (saved.npsRating ?? 0) === (this.scaleValue ?? 0) &&
      (saved.npsFeedback ?? '') === (this.feedbackText ?? '');
  }

  private isRatingChanged(): boolean {
    const saved = this.poll?.screen?.userResponse;
    if (!saved) { return true; }
    return (saved.npsRating ?? 0) !== (this.scaleValue ?? 0);
  }

  private shouldAutosaveRating(): boolean {
    const scaleSelected = (this.scaleValue ?? 0) > 0;
    const notYetSaved = !this.poll?.screen?.isCompleted;
    const reEditInProgress = this.poll?.screen?.isCompleted && this.isEdit;
    return scaleSelected && (notYetSaved || reEditInProgress) && this.isRatingChanged();
  }

  calculateValue(clientX: number): number {
    const min = this.lowerScale;
    const max = this.higherScale;
    const percentage = clientX / window.innerWidth;
    return Math.round(min + percentage * (max - min));
  }

  ngOnDestroy() {
    if (this.shouldAutosaveRating()) {
      this.saveNps(true, false);
    }
  }

  finalize() {
    // Nothing changed — exit edit mode silently. This must run BEFORE the
    // feedback-required warning: on a return visit to a completed screen whose
    // saved feedback is empty, ngOnInit forces isEdit = true, so the warning
    // would otherwise nag for feedback that is optional and hasn't changed.
    if (this.isDataUnchanged()) {
      this.isEdit = false;
      return;
    }
    if (this.isEdit && !this.feedbackText?.trim()) {
      this.tostrService.warning(this.i18n.t('Please add your feedback before finalizing.'));
      return;
    }
    this.enableFinal = false;
    this.enable = true;

    mixpanel.track('nps rating submitted', { rating: this.scaleValue, hasFeedback: !!this.feedbackText?.trim() });
    this.saveNps();
  }

  enableBlurCard() {
    this.enable = !this.enable;
    this.blurCard?.emit({ enable: this.enable, poll: this.poll });
  }

  private resizeHeight() {
    this.keyboard.onKeyboardDidShow().subscribe((info) => {
      const container = document.querySelector('.nps-wrap.nps-wrap-mobile') as HTMLElement;
      // if (this.platform.is('android')) {
      //   container?.setAttribute('style', `height: calc(100vh - ${info.keyboardHeight}px - 50px); min-height: unset; overflow-y: auto;`);
      // }
      if (this.platform.is('ios')) {
        container?.setAttribute('style', `height: calc(100vh - ${info.keyboardHeight}px); min-height: unset; overflow-y: auto;`);
      }
    });
  }

  private restoreResizeHeight() {
    this.keyboard.onKeyboardDidHide().subscribe(() => {
      const container = document.querySelector('.nps-wrap.nps-wrap-mobile') as HTMLElement;
      container?.removeAttribute('style');
    });
  }

  private persistCache() {
    const selections = this.appState.get('npsSelections') ?? {};
    selections[this.poll?.screenContentId] = { rating: this.scaleValue, feedback: this.feedbackText };
    this.appState.set('npsSelections', selections);
  }

  private saveNps(silent = false, includeFeedback = true) {
    // Guard against overlapping saves — e.g. finalize() firing a request and the
    // ngOnDestroy autosave firing a second one before the first response resolves
    // (the endpoint is not idempotent, so a stale duplicate POST must be avoided).
    if (this.isSaving) { return; }
    this.isSaving = true;
    const isReEdit = !!this.poll?.screen?.isCompleted;
    const requestData: any = {
      step_id: this.poll?.stepId,
      screen_id: this.poll?.screenId,
      screen_content_id: this.poll?.screenContentId,
      nps_rating: this.scaleValue,
    };
    if (includeFeedback) {
      requestData.nps_feedback = this.feedbackText;
    }

    this.pollService.saveNpsAnswers(requestData).subscribe(
      (response) => {
        this.isSaving = false;
        const responseData = response.data;
        this.poll.screen.isCompleted = true;
        this.poll.screen.content.isCompleted = true;

        const path = this.appState.get('currentPath');
        const stepScreens = path?.kyperChapters?.[this.chapterIndex]?.steps?.[this.stepIndex]?.screens?.[this.currentIndex]?.screenContents;
        if (stepScreens) {
          stepScreens.isCompleted = true;
          if (!stepScreens.userResponse) { stepScreens.userResponse = {}; }
          stepScreens.userResponse.npsRating = responseData?.npsRating ?? this.scaleValue;
          if (includeFeedback) {
            stepScreens.userResponse.npsFeedback = this.feedbackText;
          }
        }

        const dailyStepsData = this.appState.get('DailyStepsData');
        const dailyStepScreens = dailyStepsData?.[this.chapterIndex]?.steps?.[this.stepIndex]?.screens?.[this.currentIndex]?.screenContents;
        if (dailyStepScreens) {
          dailyStepScreens.isCompleted = true;
          if (!dailyStepScreens.userResponse) { dailyStepScreens.userResponse = {}; }
          dailyStepScreens.userResponse.npsRating = responseData?.npsRating ?? this.scaleValue;
          if (includeFeedback) {
            dailyStepScreens.userResponse.npsFeedback = this.feedbackText;
          }
        }

        this.appState.set('DailyStepsData', dailyStepsData);
        this.appState.set('currentPath', path);

        if (!this.poll.screen.userResponse) { this.poll.screen.userResponse = {}; }
        this.poll.screen.userResponse.npsRating = responseData?.npsRating ?? this.scaleValue;
        if (includeFeedback) {
          this.poll.screen.userResponse.npsFeedback = this.feedbackText;
        }

        // Data is on the server — always null out the cache entry so the component
        // initializes into read-only state on the next visit.
        if (includeFeedback) {
          const npsSelections = this.appState.get('npsSelections') ?? {};
          delete npsSelections[this.poll?.screenContentId];
          this.appState.set('npsSelections', npsSelections);
        }

        if (!silent) {
          this.tostrService.success(this.i18n.t('Feedback saved successfully.'));
          if (!isReEdit) {
            this.scaleEvent.emit({
              scaleValue: this.scaleValue?.toString(),
              question: this.poll,
              higherScale: this.higherScale.toString()
            });
          } else {
            this.isEdit = false;
          }
        }
      },
      () => {
        this.isSaving = false;
        if (!silent) { this.tostrService.error(this.i18n.t('Server or connection issue. Please check your internet connection.')); }
      }
    );
  }

  private changeSubTextFont() {
    this.questionRefs?.forEach(ref => {
      if (ref?.nativeElement) {
        this.applyStylesToAllInnerTags(ref.nativeElement);
      }
    });
  }

  private applyStylesToAllInnerTags(element: HTMLElement) {
    if (!element) { return; }
    Array.from(element.children).forEach((child: Element) => {
      const tag = child.tagName.toLowerCase();
      if (tag === 'ol' || tag === 'ul') {
        this.render.setStyle(child, 'list-style', 'none');
        this.render.setStyle(child, 'padding-left', '1.5em');
      }
      if (tag === 'li') {
        const dataList = child.getAttribute('data-list');
        if (dataList === 'bullet') {
          this.render.setStyle(child, 'list-style-type', 'disc');
          this.render.setStyle(child, 'display', 'list-item');
          this.render.setStyle(child, 'list-style-position', 'outside');
        } else if (dataList === 'ordered') {
          this.render.setStyle(child, 'list-style-type', 'decimal');
          this.render.setStyle(child, 'display', 'list-item');
          this.render.setStyle(child, 'list-style-position', 'outside');
        }
      }
      this.applyStylesToAllInnerTags(child as HTMLElement);
    });
  }
}
